import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export default function AdminSEO() {
  const [paths, setPaths] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [filter, setFilter] = useState("");
  const [selectedPath, setSelectedPath] = useState(null);
  const [meta, setMeta] = useState(null);

  const load = async () => {
    const [p, o] = await Promise.all([api.get("/admin/seo/paths"), api.get("/admin/seo/overrides")]);
    setPaths(p.data);
    setOverrides(o.data);
    if (!selectedPath && p.data.length) setSelectedPath(p.data[0].path);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  useEffect(() => {
    if (!selectedPath) return;
    api.get(`/seo/meta?path=${encodeURIComponent(selectedPath)}`).then((r) => setMeta({ ...r.data, _origPath: selectedPath }));
  }, [selectedPath]);

  const overridesByPath = useMemo(() => {
    const m = {};
    overrides.forEach((o) => { m[o.path] = o; });
    return m;
  }, [overrides]);

  const filtered = useMemo(() => {
    const f = filter.toLowerCase().trim();
    if (!f) return paths;
    return paths.filter((p) => p.path.toLowerCase().includes(f) || p.label.toLowerCase().includes(f));
  }, [paths, filter]);

  const save = async () => {
    if (!meta || !selectedPath) return;
    try {
      await api.put("/admin/seo/overrides", {
        path: selectedPath,
        title: meta.title,
        description: meta.description,
        keywords: meta.keywords,
        canonical: meta.canonical,
        og_image: meta.og_image,
        noindex: !!meta.noindex,
      });
      toast.success("SEO override saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const clear = async () => {
    if (!selectedPath) return;
    if (!window.confirm(`Clear SEO override for ${selectedPath}? It will revert to auto-detected defaults.`)) return;
    try {
      await api.delete(`/admin/seo/overrides?path=${encodeURIComponent(selectedPath)}`);
      toast.success("Cleared");
      load();
      // refetch meta to show new defaults
      const r = await api.get(`/seo/meta?path=${encodeURIComponent(selectedPath)}`);
      setMeta({ ...r.data, _origPath: selectedPath });
    } catch (e) {
      toast.error("Clear failed");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-seo">
        <header>
          <h1 className="bx-heading text-3xl font-extrabold">SEO editor</h1>
          <p className="text-slate-400 mt-1">Override page titles, descriptions, OG images & canonical per route. Auto-detected defaults from your tools + blog posts are pre-filled.</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <aside className="lg:col-span-4 bx-card p-3">
            <input data-testid="seo-filter" className="bx-input text-sm" placeholder="Filter routes…" value={filter} onChange={(e) => setFilter(e.target.value)} />
            <div className="mt-3 max-h-[600px] overflow-y-auto bx-scrollbar space-y-1">
              {filtered.map((p) => (
                <button
                  key={p.path}
                  data-testid={`seo-path-${p.path}`}
                  onClick={() => setSelectedPath(p.path)}
                  className={`w-full text-left px-3 py-2 rounded-md border ${selectedPath === p.path ? "bg-violet-500/10 border-violet-500/40" : "border-transparent hover:bg-white/5"}`}
                >
                  <div className="text-sm text-white truncate">{p.label}</div>
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-[11px] text-slate-500 truncate">{p.path}</code>
                    {overridesByPath[p.path] && <span className="text-[10px] text-violet-300 font-bold">CUSTOM</span>}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <main className="lg:col-span-8 bx-card p-5">
            {meta && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <code className="text-xs text-violet-300">{selectedPath}</code>
                  <div className="flex gap-2">
                    <button data-testid="seo-clear" onClick={clear} className="bx-btn-ghost text-xs"><Lucide.RotateCcw className="w-3.5 h-3.5 mr-1" /> Clear override</button>
                    <button data-testid="seo-save" onClick={save} className="bx-btn-primary text-xs"><Lucide.Save className="w-3.5 h-3.5 mr-1" /> Save override</button>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400">Title <span className="text-slate-600">({(meta.title || "").length}/60 ideal)</span></label>
                  <input data-testid="seo-title" className="bx-input mt-1" value={meta.title || ""} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Description <span className="text-slate-600">({(meta.description || "").length}/160 ideal)</span></label>
                  <textarea data-testid="seo-desc" className="bx-input mt-1" rows={3} value={meta.description || ""} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Keywords (comma-separated)</label>
                  <input data-testid="seo-keywords" className="bx-input mt-1" value={meta.keywords || ""} onChange={(e) => setMeta({ ...meta, keywords: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Canonical URL</label>
                  <input data-testid="seo-canonical" className="bx-input mt-1" value={meta.canonical || ""} onChange={(e) => setMeta({ ...meta, canonical: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-400">OG image URL</label>
                  <input data-testid="seo-og" className="bx-input mt-1" value={meta.og_image || ""} onChange={(e) => setMeta({ ...meta, og_image: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!meta.noindex} onChange={(e) => setMeta({ ...meta, noindex: e.target.checked })} />
                  <span className="text-slate-300">noindex (hide from search engines)</span>
                </label>

                {/* Google-style preview */}
                <div className="mt-3 bg-white p-4 rounded-lg">
                  <div className="text-[12px] text-[#202124]">{meta.canonical || "https://binarex.co" + selectedPath}</div>
                  <div className="text-[20px] text-[#1a0dab] leading-snug truncate">{meta.title}</div>
                  <div className="text-[14px] text-[#4d5156] mt-0.5 line-clamp-2">{meta.description}</div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </AdminLayout>
  );
}
